/* src/components/inbox/ContextRail.jsx
 * Inbox context rail — attribution, conversion chips, submitted info,
 * assigned, notes, stage history. Every card hides itself when its
 * backing field is empty rather than rendering a fake placeholder.
 */

import React from 'react';
import { resolveAttributionSource, ClickIdBadge, ConversionChips, dealStageLabel } from './chips';

function formatDate(timestamp) {
  if (!timestamp) return 'N/A';
  const d = timestamp.toDate ? timestamp.toDate() : timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function ContextRail({
  conversation,
  leadFormData,
  currentUserEmail,
  actionLoading,
  onClaim,
  onUnassign,
  mobile,
}) {
  const attributionSource = resolveAttributionSource(conversation.attribution);
  const formData = conversation.formData || conversation.initialFormData || leadFormData || {};
  const formEntries = Object.entries(formData).filter(([, v]) => v != null && String(v).trim() !== '');
  const conversionUploads = conversation.conversionUploads;

  return (
    <aside
      style={{
        width: mobile ? '100%' : '272px',
        flexShrink: 0,
        borderLeft: mobile ? 'none' : '1px solid var(--hairline-3)',
        borderTop: mobile ? '1px solid var(--hairline-3)' : 'none',
        background: 'var(--surface-2)',
        height: mobile ? 'auto' : '100vh',
        position: mobile ? 'static' : 'sticky',
        top: 0,
        overflowY: mobile ? 'visible' : 'auto',
        padding: '20px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {attributionSource && (
        <div>
          <div className="inbox-context-label">Attribution · the proof trail</div>
          <div className="inbox-context-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <span style={{ color: 'var(--muted)' }}>Source</span>
              <b>{attributionSource.label}</b>
            </div>
            {conversation.attribution?.utms?.utm_campaign && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--muted)' }}>Campaign</span>
                <b style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {conversation.attribution.utms.utm_campaign}
                </b>
              </div>
            )}
            {attributionSource.clickId && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--muted)' }}>Click ID</span>
                <ClickIdBadge clickIdKey={attributionSource.clickIdKey} clickId={attributionSource.clickId} />
              </div>
            )}
            {conversation.attribution?.landingUrl && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--muted)' }}>Landed</span>
                <b style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                  {conversation.attribution.landingUrl}
                </b>
              </div>
            )}
            {conversionUploads?.length > 0 && (
              <div style={{ borderTop: '1px solid var(--hairline-1)', paddingTop: '9px' }}>
                <ConversionChips conversionUploads={conversionUploads} />
              </div>
            )}
          </div>
        </div>
      )}

      {!attributionSource && conversionUploads?.length > 0 && (
        <div>
          <div className="inbox-context-label">Conversion</div>
          <div className="inbox-context-card">
            <ConversionChips conversionUploads={conversionUploads} />
          </div>
        </div>
      )}

      {formEntries.length > 0 && (
        <div>
          <div className="inbox-context-label">Submitted info</div>
          <div className="inbox-context-card">
            {formEntries.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--muted)' }}>{k}</span>
                <b style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>{String(v)}</b>
              </div>
            ))}
          </div>
        </div>
      )}

      <AssignedCard
        conversation={conversation}
        currentUserEmail={currentUserEmail}
        actionLoading={actionLoading}
        onClaim={onClaim}
        onUnassign={onUnassign}
      />

      <NotesCard conversation={conversation} />

      {conversation.stageHistory?.length > 0 && (
        <div>
          <div className="inbox-context-label">Stage history</div>
          <div className="inbox-context-card">
            {[...conversation.stageHistory].reverse().map((entry, idx) => {
              const who = entry.changedBy === currentUserEmail ? 'You' : entry.changedBy || 'Unknown';
              return (
                <div key={idx} style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                  {dealStageLabel(entry.from) || 'New'} {'\u2192'} <b style={{ color: 'var(--ink)' }}>{dealStageLabel(entry.to)}</b>
                  {entry.dealValue ? ` \u00b7 $${entry.dealValue}` : ''}
                  <div className="mono" style={{ fontSize: '10px', color: 'var(--faint)', marginTop: '2px' }}>
                    {`${who} \u00b7 ${formatDate(entry.changedAt)}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}

function AssignedCard({ conversation, currentUserEmail, actionLoading, onClaim, onUnassign }) {
  const assigned = conversation.assignedTo || '';
  const mine = assigned && currentUserEmail && assigned.toLowerCase() === currentUserEmail.toLowerCase();
  const sourceLabel = {
    round_robin: 'round-robin',
    rule: 'rule',
    claim: 'claimed',
    manual: 'assigned',
  }[conversation.assignmentSource];

  return (
    <div>
      <div className="inbox-context-label">Assigned</div>
      <div className="inbox-context-card" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--hairline-6), var(--hairline-7))',
            border: '1px solid var(--hairline-4)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '10px',
            color: 'var(--text-4)',
            flexShrink: 0,
          }}
        >
          {(assigned || '?').slice(0, 2).toUpperCase()}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {assigned || 'Unassigned'}
          </div>
          {sourceLabel && (
            <div className="mono" style={{ fontSize: '10px', color: 'var(--faint)', marginTop: '2px' }}>
              {sourceLabel}{mine ? ' · you' : ''}
            </div>
          )}
        </div>
        {!assigned && onClaim && (
          <button
            type="button"
            className="inbox-pill-btn-primary"
            disabled={actionLoading}
            onClick={() => onClaim()}
          >
            Claim
          </button>
        )}
        {assigned && onUnassign && (
          <button
            type="button"
            className="inbox-pill-btn"
            disabled={actionLoading}
            onClick={() => onUnassign()}
          >
            Unassign
          </button>
        )}
      </div>
    </div>
  );
}

function NotesCard({ conversation }) {
  if (!conversation.notes || conversation.notes.length === 0) return null;
  return (
    <div>
      <div className="inbox-context-label">Notes</div>
      <div className="inbox-context-card">
        {conversation.notes.map((note, idx) => (
          <div key={idx} style={{ paddingBottom: idx < conversation.notes.length - 1 ? '9px' : 0, borderBottom: idx < conversation.notes.length - 1 ? '1px solid var(--hairline-1)' : 'none' }}>
            <div style={{ color: 'var(--text-4)' }}>{note.text}</div>
            <div className="mono" style={{ fontSize: '10px', color: 'var(--faint)', marginTop: '4px' }}>
              {`${(note.addedBy || 'UNKNOWN').toUpperCase()} \u00b7 ${formatDate(note.addedAt)}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
